import {
    albertine,
    carlo,
    hugoboss,
    mastercard,
    type TenantConfig as NewTenantConfig,
    revolut,
    visaI2,
} from "@albertine/tenant-config";
import type { ExternalLinkPatterns } from "@albertine/tenant-config/schema";
import {
    isTenant,
    type OnboardingStepOrder,
    type OnboardingSteps,
    type ProfileSubPage,
    Tenant,
    type TrackedMembershipTierId,
} from "@albertine/types";
import type {
    ContributionAmounts,
    MemberMe,
} from "@albertine/types/openapi/concierge-v1";
import type React from "react";
import { createContext, useContext, useMemo } from "react";
import { z } from "zod";
import { ServiceLanguage } from "../albertine-i18n";

export { isTenant, Tenant };

type EnvSpecific<T> = {
    localhost: T;
    dev: T;
    staging: T;
    prod: T;
};

type TenantSpecific<T> = Record<Tenant, T>;
type ServiceLanguageSpecific<T> = Record<ServiceLanguage, T>;

export type BackgroundConfig =
    | { type: "image"; src: string; mobileSrc?: string }
    | {
          type: "video";
          src: string;
          mobileSrc?: string;
          posterSrc?: string;
          mobilePosterSrc?: string;
      }
    | { type: "component"; render: () => React.ReactNode };

export type ScreenBackgroundConfig = {
    background: {
        desktop: BackgroundConfig;
        mobile: BackgroundConfig;
    };
    backgroundColor?: {
        desktop: string;
        mobile: string;
    };
    scrollConfig?: {
        scrollDistance?: number;
        initialScale?: number;
        finalScale?: number;
        parallaxSpeed?: number;
    };
};

export type IntroSecondaryAction = "login" | "openHelp" | "openMarketingSite";

export type ProfileContributionMetric = keyof ContributionAmounts;

export type ProfileContributionSlide =
    | {
          type: "metric";
          key: string;
          metric: ProfileContributionMetric;
          image: string;
          tag: string;
          unit?: string;
          /** Receives `count` (the member's amount) for pluralisation. */
          memberLabel: string;
          /** Receives `total` (the formatted community amount). */
          communityDescription: string;
      }
    | {
          type: "brand";
          key: string;
          image: string;
          backgroundColor: string;
          icon: string;
          description: string;
      };

/** Text fields are i18n keys. */
export type ProfileContributionsConfig = {
    title: string;
    emptyDescription: string;
    slides: ProfileContributionSlide[];
};

export type TenantDependencyInjection = {
    assets: {
        icon: string;
    };
    snippets: {
        useDynamicTextColor: boolean;
    };
    avatar: {
        serviceAgentAvatarEmblem: (props?: { className?: string }) => React.ReactNode;

        serviceAgentAvatarComponent: Record<
            "XXLarge" | "XLarge" | "Large" | "Medium" | "Small",
            React.ComponentType<{ id: string | undefined; fullName?: string }>
        >;
    };
    discover: {
        screenBackground: ScreenBackgroundConfig;
    };
    concierge: {
        screenBackground: ScreenBackgroundConfig;
        footer?: {
            iconClass?: string;
        };
    };
    intro: {
        /** What the secondary button on the last intro slide does on the web. */
        secondaryAction: IntroSecondaryAction;
        help: {
            primaryButtonAction: "emailUsForHelp" | "goBack";
        };
    };
    eligibility:
        | {
              enabled: true;
          }
        | {
              enabled: false;
          };
    login: {
        i18Key: {
            phoneNumberStepDescription: string;
            phoneNumberVerificationStepDescription: string;
            emailLinkSentDescription: string;
            helpAdditionalDescription: string;
            helpAdditionalRegistrationDescription: string;
        };
        BackButtonIcon: (className?: string) => React.ReactNode;
        preferredLoginMethod: "email" | "phoneNumber";
    };
    onboarding: {
        OnboardingButtonIcon: () => React.ReactNode;
        screenBackground?: ScreenBackgroundConfig;
        stepOrder: OnboardingStepOrder;
        stepOverrides?: Partial<
            Record<OnboardingSteps, (member: MemberMe) => OnboardingSteps>
        >;
    };
    profile: {
        showMembershipRenewal: boolean;
        showBenefits: boolean;
        showDeleteAccount?: boolean;
        languageSelector: boolean;
        screenBackground: ScreenBackgroundConfig;
        footerIcon: {
            src: string;
            alt: string;
            className?: string;
        };
        items: {
            key: Exclude<ProfileSubPage, "benefits" | "delete-account">;
            label: string;
        }[];
        contributions?: ProfileContributionsConfig;
    };
};

export type TenantConfig = {
    authentication: {
        webUrl: EnvSpecific<string>;
        iosLinkDomain: EnvSpecific<string>;
        iosBundleId: string;
        tokenSignup:
            | {
                  enabled: true;
                  membershipTierId: TrackedMembershipTierId;
              }
            | {
                  enabled: false;
              };
    };
    allowedServiceLanguages: ServiceLanguage[];
    htmlFavicon: string;
    serviceName: string;
    serviceMarketingUrl: string;
    // iOS Universal link should open the app directly if installed
    openAppConfig: EnvSpecific<{
        iosDeepLink: string[];
        iosFallbackUrl: string;
        // Android App Link should open the app directly if installed
        androidFallbackUrl: string;
    }>;
    emailOpenAppLink: EnvSpecific<string>;
    publicShareUrlBase: EnvSpecific<string>;
    emailService: string;
    emailServiceName: string;
    externalLinks: {
        termsAndConditions: ServiceLanguageSpecific<string>;
        privacyPolicy: ServiceLanguageSpecific<string>;
        permissionPolicy?: ServiceLanguageSpecific<string> | null;
    };
    externalLinkPatterns: ExternalLinkPatterns;
};

function buildLegacyTenantConfig(config: NewTenantConfig): TenantConfig {
    return {
        serviceName: config.name,
        authentication: {
            webUrl: config.authentication.webUrl,
            iosLinkDomain: config.authentication.iosLinkDomain,
            iosBundleId: config.authentication.iosBundleId,
            tokenSignup:
                config.authentication.tokenSignup !== null
                    ? {
                          enabled: true,
                          // Trying to validate the membershipTierId by using
                          // `TrackedMembershipTierId.parse(...)` breaks the whole
                          // build of not just one but multiple packages because
                          // there are circular dependencies.
                          membershipTierId: config.authentication.tokenSignup
                              .membershipTierId as TrackedMembershipTierId,
                      }
                    : {
                          enabled: false,
                      },
        },
        allowedServiceLanguages: z
            .array(ServiceLanguage)
            .parse(config.allowedServiceLanguages),
        htmlFavicon: config.htmlFavicon,
        serviceMarketingUrl: config.serviceMarketingUrl,
        openAppConfig: config.openAppConfig,
        emailOpenAppLink: config.emailOpenAppLink,
        publicShareUrlBase: config.public_share_base_url,
        emailService: config.emailService,
        emailServiceName: config.emailServiceName,
        externalLinks: config.externalLinks,
        externalLinkPatterns: config.external_link_patterns ?? [],
    };
}

type TenantContextType = {
    tenant: Tenant;
    dependencyInjection: TenantDependencyInjection | undefined;
    tenantConfig: TenantConfig;
};

const TenantConfigs: TenantSpecific<TenantConfig> = {
    albertine: buildLegacyTenantConfig(albertine),
    hugoboss: buildLegacyTenantConfig(hugoboss),
    mastercard: buildLegacyTenantConfig(mastercard),
    carlo: buildLegacyTenantConfig(carlo),
    "visa-i2": buildLegacyTenantConfig(visaI2),
    revolut: buildLegacyTenantConfig(revolut),
};

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider(props: {
    value: {
        tenant: Tenant;
        dependencyInjection: TenantDependencyInjection | undefined;
    };
    children: React.ReactNode;
}) {
    const { children, value } = props;

    const memoizedValue = useMemo(
        () => ({
            tenant: value.tenant,
            dependencyInjection: value.dependencyInjection,
            tenantConfig: TenantConfigs[value.tenant] || TenantConfigs.albertine,
        }),
        [value],
    );

    return (
        <TenantContext.Provider value={memoizedValue}>
            {children}
        </TenantContext.Provider>
    );
}

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error("useTenant must be used within an TenantProvider");
    }
    return context;
};

export async function getTenantConfig(tenant: Tenant): Promise<TenantConfig> {
    return Promise.resolve(TenantConfigs[tenant] || TenantConfigs.albertine);
}
